import {
  type LotoAttachment,
  type LotoReport,
  type LotoReportEntry,
  lotoReportEntrySchema,
  type LotoReportScope,
} from './lotoReportSchemas'
import { lotoScopeTags } from './lotoReportScope'

export type LotoReportFields = Partial<
  Omit<
    LotoReport,
    'created_at' | 'attachments' | 'isolation_points' | 'target_equipment'
  >
>

export function createLotoReportDraft(
  scope: LotoReportScope,
  name: string
): LotoReportEntry {
  return lotoReportEntrySchema.parse({
    id: `loto-report-${crypto.randomUUID()}`,
    type: 'loto-report',
    tags: lotoScopeTags(scope),
    data: {
      name,
      created_at: new Date().toISOString(),
      attachments: [],
      isolation_points: [],
    },
  })
}

export function mergeAttachments(
  existing: LotoAttachment[],
  additions: LotoAttachment[]
): LotoAttachment[] {
  const systemDocumentIdToPageIds = new Map<string, string[]>()
  for (const { system_document_id, page_ids } of [...existing, ...additions]) {
    const current = systemDocumentIdToPageIds.get(system_document_id) ?? []
    systemDocumentIdToPageIds.set(system_document_id, [
      ...new Set([...current, ...page_ids]),
    ])
  }
  return [...systemDocumentIdToPageIds].map(
    ([system_document_id, page_ids]) => ({
      system_document_id,
      page_ids,
    })
  )
}

export function removeAttachedPage(
  report: LotoReport,
  systemDocumentId: string,
  pageId: string
): LotoReport {
  const attachments = report.attachments
    .map((attachment) =>
      attachment.system_document_id === systemDocumentId
        ? {
            ...attachment,
            page_ids: attachment.page_ids.filter((id) => id !== pageId),
          }
        : attachment
    )
    .filter((attachment) => attachment.page_ids.length > 0)
  return { ...report, attachments }
}

export function addAttachments(
  report: LotoReport,
  additions: LotoAttachment[]
): LotoReport {
  return {
    ...report,
    attachments: mergeAttachments(report.attachments, additions),
  }
}

export function updateReportFields(
  report: LotoReport,
  fields: LotoReportFields
): LotoReport {
  return { ...report, ...fields }
}
